"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import BoostIcon from "@/components/common/icons/BoostIcon";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropBoostMutation } from "@/hooks/drops/useDropBoostMutation";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
interface WaveDropActionsBoostProps {
  readonly drop: ExtendedDrop;
  readonly showCount?: boolean;
}
const WaveDropActionsBoost: React.FC<WaveDropActionsBoostProps> = t0 => {
  const $ = _c(42);
  const {
    drop,
    showCount: t1
  } = t0;
  const showCount = t1 === undefined ? true : t1;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const {
    toggleBoost,
    isPending
  } = useDropBoostMutation();
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef(null);
  let t2;
  if ($[0] !== drop.id) {
    t2 = drop.id.startsWith("temp-");
    $[0] = drop.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isTemporaryDrop = t2;
  const canBoost = !isTemporaryDrop && !!connectedProfile;
  const isBoosted = drop.context_profile_context?.boosted ?? false;
  const boostCount = drop.boosts;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== canBoost || $[5] !== drop || $[6] !== isPending || $[7] !== toggleBoost) {
    t5 = () => {
      if (!canBoost || isPending) {
        return;
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      setIsAnimating(true);
      toggleBoost(drop);
      animationTimeoutRef.current = setTimeout(() => setIsAnimating(false), 300);
    };
    $[4] = canBoost;
    $[5] = drop;
    $[6] = isPending;
    $[7] = toggleBoost;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleClick = t5;
  const tooltipId = `boost-drop-${drop.id}`;
  const t6 = `tw-group/boost tw-flex tw-h-7 tw-min-w-7 tw-items-center tw-justify-center tw-gap-x-1 tw-rounded-full tw-border-0 tw-bg-transparent tw-px-1.5 tw-text-xs tw-font-medium tw-leading-5 tw-transition-all tw-duration-200 tw-ease-out desktop-hover:hover:tw-bg-iron-800 ${canBoost ? "tw-cursor-pointer" : "tw-cursor-default tw-opacity-50"} ${isBoosted ? "tw-text-orange-400 desktop-hover:hover:tw-text-orange-300" : "tw-text-iron-400 desktop-hover:hover:tw-text-orange-400"}`;
  const t7 = !canBoost || isPending;
  const t8 = isBoosted ? "Remove boost from drop" : "Boost drop";
  let t9;
  if ($[9] !== canBoost || $[10] !== tooltipId) {
    t9 = canBoost ? {
      "data-tooltip-id": tooltipId
    } : {};
    $[9] = canBoost;
    $[10] = tooltipId;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = isBoosted ? "boosted" : "not-boosted";
  let t11;
  if ($[12] !== isAnimating) {
    t11 = isAnimating ? {
      scale: 0.5,
      opacity: 0
    } : false;
    $[12] = isAnimating;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      scale: 1,
      opacity: 1
    };
    t13 = {
      scale: 0.5,
      opacity: 0
    };
    t14 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t12 = $[14];
    t13 = $[15];
    t14 = $[16];
  }
  const t15 = `tw-size-5 tw-flex-shrink-0 tw-transition tw-duration-300 tw-ease-out ${isBoosted ? "tw-drop-shadow-[0_0_4px_rgba(251,146,60,0.5)]" : ""}`;
  const t16 = isBoosted ? "filled" : "outlined";
  let t17;
  if ($[17] !== t15 || $[18] !== t16) {
    t17 = <BoostIcon className={t15} variant={t16} />;
    $[17] = t15;
    $[18] = t16;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t17) {
    t18 = <AnimatePresence mode="wait"><motion.div key={t10} initial={t11} animate={t12} exit={t13} transition={t14} className="tw-flex tw-items-center">{t17}</motion.div></AnimatePresence>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== boostCount || $[25] !== showCount) {
    t19 = showCount && boostCount > 0 && <motion.span key={boostCount} initial={{
      opacity: 0,
      y: -5
    }} animate={{
      opacity: 1,
      y: 0
    }} className="tw-text-xs tw-font-medium">{boostCount}</motion.span>;
    $[24] = boostCount;
    $[25] = showCount;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== handleClick || $[28] !== t18 || $[29] !== t19 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t20 = <button className={t6} onClick={handleClick} disabled={t7} aria-label={t8} {...t9}>{t18}{t19}</button>;
    $[27] = handleClick;
    $[28] = t18;
    $[29] = t19;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== canBoost || $[36] !== isBoosted || $[37] !== tooltipId) {
    t21 = canBoost && <Tooltip id={tooltipId} place="top" positionStrategy="fixed" offset={8} opacity={1} style={{
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    }}><span className="tw-text-xs">{isBoosted ? "Remove Boost" : "Boost"}</span></Tooltip>;
    $[35] = canBoost;
    $[36] = isBoosted;
    $[37] = tooltipId;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t20 || $[40] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  return t22;
};
export default WaveDropActionsBoost;